import { useTranslation } from 'react-i18next';
import { Typography } from '@/components';
import { OpportunityDetails } from '@/features/opportunities/types';
import { AllowedStatus } from '@/features/offers/types';
import { PaginatedOffersList } from './paginated-offers-list';
import { Fragment } from 'react/jsx-runtime';

type OpportunityBiddersCardProps = {
  readonly opportunity: OpportunityDetails;
};

const OFFERS_PER_PAGE = 5;

export function OpportunityBiddersCard({ opportunity }: OpportunityBiddersCardProps) {
  const { t } = useTranslation(['opportunities']);

  if (!opportunity) return null;

  const allowedStatuses: AllowedStatus[] = ['accepted', 'rejected', 'pending'];

  const filteredOffers = opportunity.offers.filter((offer) =>
    allowedStatuses.includes(offer.status?.name as AllowedStatus)
  );

  const sortedOffers = [...filteredOffers].sort((a, b) => {
    const dateA = new Date(a.createdAt ?? '');
    const dateB = new Date(b.createdAt ?? '');
    return dateB.getTime() - dateA.getTime();
  });

  const statusMessage = [
    {
      status: 'draft',
      label: t(
        'This project is still in draft. \nComplete all required information and select a plan to make it visible to investors and start receiving Expressions of Interest.'
      ),
    },
    {
      status: 'pending',
      label: t(
        'Your project is under review by the nTeaser team.\nOnce approved, it will be published and start receiving Expressions of Interest.'
      ),
    },
    {
      status: 'published',
      label: t(
        "Your project is now live, but no Expressions of Interest have been received yet. \nYou'll see them listed here as soon as they come in."
      ),
    },
  ];

  const opportunityStatus = statusMessage.find((item) => item.status === opportunity.status.name);

  return (
    <div className="rounded-lg border bg-white p-6 shadow-xs">
      <Typography as="h3" weight="semibold" size="title" className="uppercase">
        {t('Received Offers')}
      </Typography>
      <Typography as="p" size="body-3" color="secondary" weight="normal" className="mb-6">
        {t('Here you can find all the offers received for this opportunity.')}
      </Typography>

      <div className="grid grid-cols-1">
        {filteredOffers.length > 0 ? (
          <PaginatedOffersList items={sortedOffers} itemsPerPage={OFFERS_PER_PAGE} />
        ) : opportunityStatus ? (
          <Typography as="p" size="body-3" color="primary" weight="normal">
            {opportunityStatus.label.split('\n').map((line, index) => (
              <Fragment key={index}>
                {line}
                <br />
              </Fragment>
            ))}
          </Typography>
        ) : (
          <Typography as="p" size="body-3" color="primary" weight="normal">
            {t('No offers received yet')}
          </Typography>
        )}
      </div>
    </div>
  );
}
